import { atom } from 'jotai';
import { Getter } from 'jotai/vanilla';
import { filtersAtom } from '@/atoms/filters/filter';
import { filterAtomDefaults } from '@/consts/defaults/filterAtomDefaults';
import { GAME_RANGE_OPTIONS } from '@/consts/gameRange';
import { ConfigEventFilterSet, ConfigFilterSet, ConfigGameFilterSet, ConfigPlayerFilterSet } from '@/types/userConfigs';
import { convertOptionsToValues } from '@/utils/array';
import { convertGameFilterValuesToPersistedValues } from '@/utils/persistedFilters';
import { comparisonFiltersAtom } from './comparison';
import { selectedCompetitionsAtom } from './highLevel/competitions';
import { seasonIdsToFilterByAtom } from './highLevel/seasons';

const gameFilterValuesToPersist = (get: Getter): ConfigGameFilterSet => {
  const { gameRange, periodRange, gameType, startDate, endDate, deselectedGameIds } = get(filtersAtom);
  const isPeriod = gameRange.key === GAME_RANGE_OPTIONS.period.key;
  const isDateRange = gameRange.key === GAME_RANGE_OPTIONS.dateRange.key;
  return {
    ...convertGameFilterValuesToPersistedValues({
      periodRangeToConvert: isPeriod ? periodRange : filterAtomDefaults.periodRange,
      gameRangeToConvert: gameRange,
      gameTypeToConvert: gameType,
    }),
    startDate: isDateRange ? startDate : filterAtomDefaults.startDate,
    endDate: isDateRange ? endDate : filterAtomDefaults.endDate,
    deselectedGameIds: convertOptionsToValues(deselectedGameIds),
  };
};

const eventFilterValuesToPersist = (
  get: Getter,
): ConfigEventFilterSet & Pick<ConfigFilterSet, 'matchPeriods' | 'minMatchMinute' | 'maxMatchMinute'> => {
  const {
    eventNames,
    outcomes,
    bodyParts,
    playPatterns,
    pitchStartZones,
    pitchEndZones,
    minObv,
    maxObv,
    minXg,
    maxXg,
    eventFilterGroups,
    matchPeriods,
    minMatchMinute,
    maxMatchMinute,
  } = get(filtersAtom);
  return {
    eventNames,
    outcomes,
    bodyParts,
    playPatterns,
    pitchStartZones,
    pitchEndZones,
    minObv,
    maxObv,
    minXg,
    maxXg,
    eventFilterGroups,
    matchPeriods,
    minMatchMinute,
    maxMatchMinute,
  };
};

const playerFilterValuesToPersist = (get: Getter): ConfigPlayerFilterSet => {
  const { minAge, maxAge, minHeight, maxHeight, minMinutes, maxMinutes, nationality, preferredFoot } = get(filtersAtom);
  return { minAge, maxAge, minHeight, maxHeight, minMinutes, maxMinutes, nationality, preferredFoot };
};

export const filtersToPersistAtom = atom(get => {
  const { playerIds, teamIds, positions, metric, metricDistributions, matchPeriods, minMatchMinute, maxMatchMinute } =
    get(filtersAtom);
  const persistValues: ConfigFilterSet = {
    // Deliberately not using the competitionIdsToFilterByAtom here as when no competitions are selected,
    // we pass up all the available competition options in the query to ensure we only get data for the gender
    // we want. But we don't want to persist all those competition ids, so using selectedCompetitionsAtom means
    // we persist the selection that is shown on the page. Filtering by gender still works on the report as we also
    // persist the gender which is then used to populate available competition options for the widget.
    competitionIds: get(selectedCompetitionsAtom).map(({ value }) => value),
    seasonIds: get(seasonIdsToFilterByAtom),
    playerIds,
    teamIds,
    positions,
    metric,
    metricDistributions,
    ...gameFilterValuesToPersist(get),
    ...eventFilterValuesToPersist(get),
    ...playerFilterValuesToPersist(get),
    matchPeriods,
    minMatchMinute,
    maxMatchMinute,
  };
  return persistValues;
});

export const filterChangesToApplyAtom = atom(get => {
  const { startDate, endDate } = get(filtersAtom);
  const persistValues: ConfigFilterSet = {
    ...get(filtersToPersistAtom),
    /* these are to override the startDate and endDate values from
    gameFilterValuesToPersist as we don't want the selectedGameRange === 'dateRange'
    logic where this atom is used */
    startDate,
    endDate,
  };
  return persistValues;
});

export const lastAppliedFiltersAtom = atom<ConfigFilterSet | null>(null);

// Serialises the comparison entity's game-level filters into the same format
// expected by getFilterAtomsToHydrate, so the vis page opens with the correct games.
export const comparisonGameFiltersToPersistAtom = atom(get => {
  const { gameType, gameRange, periodRange, startDate, endDate, deselectedGameIds, positions } =
    get(comparisonFiltersAtom);
  const isPeriod = gameRange.key === GAME_RANGE_OPTIONS.period.key;
  const isDateRange = gameRange.key === GAME_RANGE_OPTIONS.dateRange.key;
  return {
    ...convertGameFilterValuesToPersistedValues({
      periodRangeToConvert: isPeriod ? periodRange : filterAtomDefaults.periodRange,
      gameRangeToConvert: gameRange,
      gameTypeToConvert: gameType,
    }),
    startDate: isDateRange ? startDate : filterAtomDefaults.startDate,
    endDate: isDateRange ? endDate : filterAtomDefaults.endDate,
    deselectedGameIds: convertOptionsToValues(deselectedGameIds),
    positions,
  };
});

const filterStateHistoryBaseAtom = atom<{ pathname: string; filtersToPersist: ConfigFilterSet }[]>([]);

export const filterStateHistoryAtom = atom(
  get => get(filterStateHistoryBaseAtom),
  (get, set, pathname: string) => {
    const filterStateHistory = get(filterStateHistoryBaseAtom);
    const filtersToPersist = get(filtersToPersistAtom);

    const newHistoryItem = { pathname, filtersToPersist };

    /* this and the `isPathNameDifferent` checks are both to add a new item
    we are doing this check first as if we don't the lastHistory item line
    will fail due to not having any items in filterStateHistoryHistory */
    if (filterStateHistory.length === 0) {
      set(filterStateHistoryBaseAtom, [...filterStateHistory, newHistoryItem]);
      return;
    }

    const lastHistoryItem = filterStateHistory[filterStateHistory.length - 1];
    const isPathNameDifferent = lastHistoryItem.pathname !== pathname;

    if (isPathNameDifferent) {
      set(filterStateHistoryBaseAtom, [...filterStateHistory, newHistoryItem]);
    } else {
      const newHistory = filterStateHistory.with(filterStateHistory.length - 1, newHistoryItem);
      set(filterStateHistoryBaseAtom, newHistory);
    }
  },
);

export const clearLastFilterStateHistoryAtom = atom(null, (get, set) => {
  const currentHistory = get(filterStateHistoryAtom);
  set(filterStateHistoryBaseAtom, currentHistory.slice(0, currentHistory.length - 1));
});
